import { useRef, useState, useEffect } from 'react';
import { autorun } from 'mobx';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import { DialogContent } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { observer } from "mobx-react-lite";
import DialogActions from '@material-ui/core/DialogActions';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import { StateService } from "../services/StateService";
import style from "./Slots.module.scss";
import { Toast, ToastProps } from './Toast';
import { FET_ERC20 } from '../dutch-auction/constants';
import BN from "bn.js";
import { InfoBox } from './InfoBox';

import slotsBackground from '../assets/box-bg.png';


const useStyles = makeStyles({
  slotsSection: {
    width: '100%',
    color: 'white',
    borderRadius: '20px',
    padding: '25px 40px',
    background: `url(${slotsBackground}) no-repeat left top`,
    backgroundSize: 'cover',
    backgroundColor: 'rgba(236, 159, 215, 0.08)',
    boxShadow: '0px 100px 90px rgba(34, 8, 31, 0.236943)',
    backdropFilter: 'blur(70.6753px)',
    marginTop: '55px',
  },
  taglinebox: {
    fontFamily: 'Gilroy-Regular, sans-serif',
    fontSize: '18px',
    lineHeight: '26px',
    padding: '0 10px',
  },
  cardTitle: {
    color: '#f782ed',
    fontSize: '32px',
    textAlign: 'center',
    fontWeight: 500,
    fontFamily: 'Gilroy-Medium, sans-serif',
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
  },
  slotDetails: {
    paddingTop: 0,
  },
  slotTitleLabel: {
    fontFamily: 'Gilroy-Regular, sans-serif',
    fontSize: '20px',
  },
  PlaceBidForm: {
    margin: '25px 0',
  },
  placeBidHr: {
    marginTop: '25px',
    display: 'inline-block',
    width: "85%"
  },
  disabledPlaceBidButton: {
    color: "rgba(255, 255, 255, 0.4) !important"
  },
  auctionPhase: {
    fontFamily: 'Gilroy-Regular, sans-serif',
  },
  currentPrice: {
    fontFamily: 'Gilroy-Regular, sans-serif',
    margin: '8px'
  },
  placeBid: {
    padding: '10px 0',
  },
  currentFetPrice: {
    border: "1px solid #544A56",
    borderRadius: "20px",
    margin: "8px 0px 6px 0px",
    display: "table-cell",
    textAlign: "center",
    justifyContent: "center",
    maxWidth: "100%",
    minWidth: '200px',
    padding: "1px 20px 1px 20px",
  },
  currentPrice1: {
    position: "relative",
    marginBottom: "15px",
    display: "flex"
  },
  currentPriceText: {
    color: "#F782ED",
    fontSize: "16px",
    fontFamily: 'Gilroy-Medium, sans-serif',
    marginBottom: 0,
    marginTop: "17px",
    textAlign: "left",
  },
  Price: {
    color: "#FFFFFF",
    fontSize: "55px",
    fontFamily: 'Gilroy-Regular, sans-serif',
    lineHeight: "60px",
    marginBottom: 0,
  },
  Fet: {
    color: "#FFFFFF",
    fontSize: "22px",
    fontFamily: 'Gilroy-Medium, sans-serif',
    transform: "translate(10%, 0)",
  },
  MainFetPrice: {
    position: "relative",
    justifyContent: "center",
    display: "flex",
  },
  priceTextWrapper: {
    display: 'inline-block',
    margin: '0 auto'
  },
  pleaseWait: {
    display: "flex",
    color: "#FFFFFF",
    fontFamily: 'Gilroy-Medium, sans-serif',
    alignItems: 'center',
  }
});

interface SlotsProps {
  state: StateService;
}

export const Slots = observer((props: SlotsProps) => {
  const classes = useStyles();
  const [amount, setAmount] = useState(0);
  const messageToast = useRef<ToastProps>(null);
  const [alreadyBid, setAlreadyBid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [ withdrawDisabled, setWithdrawDisabled ] = useState(true);
  const [ withdrawAmount, setWithdrawAmount ] = useState(0);
  const [withdrawAlert, setWithdrawAlert] = useState<boolean>(false);

  const finalPrice = props.state.bidStore.getAuctionInfo().finalPrice !== 0
      ? (new BN(props.state.bidStore.getAuctionInfo().finalPrice)).div(FET_ERC20.multiplier).toNumber()
      : Number(0);

  const submitBid = async (event: any) => {
    event.preventDefault();

    let address = props.state.authentication.getAddress();
    if (address === "NONE" || !props.state.web3.isAvailable()) {
      messageToast.current?.showMessage("You need to log in first!", 'error');
      return;
    }
    try {
      let latest = props.state.priceStore.getCurrentPrice();
      if (amount < latest) {
        messageToast.current?.showMessage(`Can't bid below current price! Enter higher than: ${latest}!`, 'error');
        return;
      }
      setIsLoading(true);
      let [status, status_message] = await props.state.web3.getContract()?.bid(address, amount) ?? [false, "Contract not defined"];
      if (status) {
        messageToast.current?.showMessage('Successful bid!', 'info');
        await props.state.contractSync();
      } else {
        messageToast.current?.showMessage(status_message, 'error');
      }
    } catch (exception) {
      console.log("Bid failed: ", exception);
      messageToast.current?.showMessage("Failed to bid!", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const withdraw = async (event: any) => {
    event.preventDefault();
    try {
      let address = props.state.authentication.getAddress();
      if (address === "NONE" || !props.state.web3.isAvailable()) {
        messageToast.current?.showMessage("Login first!", 'error');
        return;
      }
      await props.state.updateSelfDepositAndStake(address);
      const [ selfDeposit, selfStakeNeeded ] = props.state.bidStore.getSelfDepositAndStake();

      if (selfStakeNeeded >= selfDeposit) {
        setWithdrawDisabled(true);
        messageToast.current?.showMessage("Can't withdraw! Balance locked!", 'error');
        return;
      }
      setIsLoading(true);
      let status = await props.state.web3.getContract()?.withdrawSelfStake(address);
      if (status) {
        messageToast.current?.showMessage("Successful withdraw!", 'info');
      } else {
        messageToast.current?.showMessage("Failed to withdraw!", 'error');
      }
      await props.state.updateSelfDepositAndStake(address);
      const r = props.state.bidStore.getSelfDepositAndStake();
      setWithdrawDisabled(r[0] <= r[1]);
      setWithdrawAmount(r[0] - r[1]);
    } catch (exception) {
      console.log("Withdraw failed: ", exception);
      messageToast.current?.showMessage("Failed to withdraw!", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const disposer = autorun(() => {
      let address = props.state.authentication.getAddress();
      if (address === "NONE") {
        return;
      }
      setAlreadyBid(props.state.bidStore.didBid(address));
      const [ selfDeposit, selfStakeNeeded ] = props.state.bidStore.getSelfDepositAndStake();

      setWithdrawDisabled( selfDeposit <= selfStakeNeeded );
      setWithdrawAmount( selfDeposit - selfStakeNeeded );
    });
    return () => { disposer(); };
  }, [props.state.authentication, props.state.bidStore]);


  return (
    <div className={classes.slotsSection}>
      <Dialog
        open={withdrawAlert}
        onClose={setWithdrawAlert}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Are you sure you want to withdraw?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Withdrawing your spare FET costs a gas fee of roughly 0.0024 ETH ($7). If you have staked FET you will
            be able to withdraw your stake and the spare FET in one transaction after the lockup ends on 24th September.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setWithdrawAlert(false);
            }}
            variant="contained"
            color="secondary"
          >
            reject
          </Button>
          <Button
            onClick={(event: any) => {
              setWithdrawAlert(false);
              withdraw(event);
            }}
            variant="contained"
            color="primary" 
            autoFocus
          >
            proceed
          </Button>
        </DialogActions>
      </Dialog>
      <Toast ref={messageToast} />
      <Typography gutterBottom variant="h3" component="h3" className={classes.cardTitle}>
        Place Your Bid
        <InfoBox
          title="Place your bid here. Your bid needs to be above the current price. You can bid for multiple slots
          by bidding a multiple of the current price."
        />
      </Typography>

      { props.state.bidStore.isBiddingPhase() && props.state.rounds.isAuctionPhase() ? (
      <p className={classes.taglinebox}>Bid here to enter the Fetch NFT dutch auction! Your bid must be higher than or
        equal to the asking price, which will decrease with time. Once the slots sell out the auction will close. As the
        current price decreases, the number of predicted slots each bidder has will increase.
      </p>
          )
      : (<p className={classes.auctionPhase}>Bidding has ended and the auction is closed.</p>)
      }

      { props.state.web3.isAvailable() ?
      <div className={classes.slotDetails}>
        <div className={classes.MainFetPrice}>
          <div className={classes.currentFetPrice}>
            <div className={classes.priceTextWrapper}>
              {props.state.bidStore.isFinalized() && finalPrice !== 0 ?
                  <div>
                    <p className={classes.currentPriceText}>Final price</p>
                    <div className={classes.currentPrice1}>
                      <span className={classes.Price}>{finalPrice}</span>
                      <span className={classes.Fet}>FET</span>
                    </div>
                  </div>
                  :
                  <div>
                    <p className={classes.currentPriceText}>Current price</p>
                    <div className={classes.currentPrice1}>
                      <span className={classes.Price}>{props.state.priceStore.getCurrentPrice()}</span>
                      <span className={classes.Fet}>FET</span>
                    </div>
                  </div>
              }
            </div>
          </div>
        </div>
        <hr className={classes.placeBidHr} />
        { props.state.bidStore.isBiddingPhase() && props.state.rounds.isAuctionPhase() &&
          <div>
            {
              alreadyBid
                ? (
                    <p className={classes.currentPrice}>You have placed your bid.</p>
                )
                : (
                  <form className={style.PlaceBidForm}>
                    <Grid container spacing={0}>
                      <Grid item xs={7}>
                        <TextField
                          type="number"
                          value={amount}
                          onChange={(e) => setAmount(Number.parseFloat(e.target.value))}
                          id="standard-required"
                          label="Enter a bid"
                          InputLabelProps={{ shrink: true, }}
                          variant="outlined"
                          fullWidth
                          onKeyDown={(e) => { if (e.key === "Enter") {submitBid(e)} }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <Button
                          classes={{ disabled: classes.disabledPlaceBidButton }}
                          className={classes.placeBid}
                          variant="contained"
                          color="secondary"
                          onClick={submitBid}
                          disabled={Number.isNaN(amount) || amount < props.state.priceStore.getCurrentPrice() || props.state.bidStore.getAvailableSlots() === 0}
                          fullWidth
                        >
                          Place Bid
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                )
            }
          </div>
        }

        {!props.state.rounds.isAuctionPhase() &&
          <div className={style.FinalizedDiv}>
            {props.state.authentication.getAddress() !== "NONE" && withdrawAmount > 0 && (
              <Button disabled={withdrawDisabled} variant="contained" onClick={() => setWithdrawAlert(true)}>Get change: ({withdrawAmount.toFixed(2).toString()} FET)</Button>
            )}
          </div>
        }
      </div>
      : <p className={classes.taglinebox}>
          Please connect your wallet to be able to bid.
          </p>
      }

      <Dialog
        fullScreen={fullScreen}
        open={isLoading}
        style={{
          margin: "auto",
        }}
        PaperProps={{
          style: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}>
        <DialogContent className={classes.pleaseWait}><div>Please wait! Transaction confirmation can take a while.
          <CircularProgress disableShrink /></div>
        </DialogContent>
      </Dialog>
    </div>
  );
});
